/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useVoiceRecognition } from './useVoiceRecognition';

interface UseRequestProcessorOptions {
  onProcess: (request: string) => Promise<void>;
  onSuccess?: () => void;
  // Optional external state for persisting request value across navigation
  externalValue?: string;
  onExternalValueChange?: (value: string) => void;
}

export function useRequestProcessor({ onProcess, onSuccess, externalValue, onExternalValueChange }: UseRequestProcessorOptions) {
  // Use external state if provided, otherwise use local state
  const [localRequest, setLocalRequest] = useState('');
  const request = externalValue !== undefined ? externalValue : localRequest;
  const setRequest = onExternalValueChange || setLocalRequest;
  
  // Use a ref to track current request value for the transcript handler
  // This allows us to append to the current value without needing the callback form of setState
  const requestRef = useRef(request);
  useEffect(() => {
    requestRef.current = request;
  }, [request]);
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastResponse, setLastResponse] = useState('');

  const handleTranscript = useCallback((transcript: string) => {
    const prev = requestRef.current;
    setRequest(prev ? `${prev} ${transcript}` : transcript);
  }, [setRequest]);

  const { isListening, isSupported: speechSupported, toggleListening } = useVoiceRecognition(handleTranscript);

  const handleProcess = useCallback(async () => {
    if (!request.trim() || isProcessing) return;

    setIsProcessing(true);
    setLastResponse('');

    try {
      await onProcess(request);
      setRequest('');
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('[Request Processor] Error:', error);
      setLastResponse(`Error: ${error.message || 'Failed to process request'}`);
    } finally {
      setIsProcessing(false);
    }
  }, [request, isProcessing, onProcess, onSuccess]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleProcess();
    }
  }, [handleProcess]);

  const setResponse = useCallback((response: string) => {
    setLastResponse(response);
  }, []);

  return {
    request,
    setRequest,
    isProcessing,
    lastResponse,
    setResponse,
    isListening,
    speechSupported,
    handleProcess,
    handleKeyPress,
    toggleListening
  };
}

